import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  Calendar, Users, AlertTriangle, X, CheckCircle, Clock, MapPin, ExternalLink, MessageSquare, Send, Camera, Image 
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Image Modal Component for enlarging images
const ImageModal = ({ imageUrl, onClose }) => {
  if (!imageUrl) return null;
  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4"
      onClick={onClose}
    >
      <div className="relative max-w-4xl max-h-[90vh]">
        <button 
          onClick={onClose}
          className="absolute -top-10 right-0 p-2 text-white hover:text-gray-300"
        >
          <X className="w-6 h-6" />
        </button>
        <img 
          src={imageUrl} 
          alt="Enlarged view" 
          className="max-w-full max-h-[85vh] object-contain rounded-lg"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
};

// Active Cases Panel - displays unresolved agronomist cases for farmer
export const ActiveCasesPanel = () => {
  const { getAuthHeaders, token } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCase, setSelectedCase] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [pendingImages, setPendingImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCases();
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const fetchCases = async () => {
    try {
      const response = await axios.get(`${API}/cases`, {
        headers: getAuthHeaders()
      });
      // Filter to only unresolved cases
      const activeCases = response.data.filter(c => c.status !== 'resolved');
      setCases(activeCases);
    } catch (error) {
      console.error('Error fetching cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCaseChat = async (caseItem) => {
    setSelectedCase(caseItem);
    try {
      const response = await axios.get(`${API}/cases/${caseItem.id}/messages`, {
        headers: getAuthHeaders()
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Failed to load messages');
    }
  };

  const closeCaseChat = () => {
    setSelectedCase(null);
    setMessages([]);
    setNewMessage('');
    setPendingImages([]);
  };

  // Handle photo upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      setPendingImages(prev => [...prev, response.data.url]);
      toast.success('Photo added');
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Remove pending photo
  const removePendingImage = (index) => {
    setPendingImages(prev => prev.filter((_, i) => i !== index));
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && pendingImages.length === 0) || !selectedCase) return;
    
    setSendingMessage(true);
    try {
      await axios.post(`${API}/cases/${selectedCase.id}/messages`, {
        text: newMessage || (pendingImages.length > 0 ? '📷 Photo' : ''),
        image_urls: pendingImages
      }, {
        headers: getAuthHeaders()
      });
      setNewMessage('');
      setPendingImages([]);
      // Refresh messages
      const response = await axios.get(`${API}/cases/${selectedCase.id}/messages`, {
        headers: getAuthHeaders()
      });
      setMessages(response.data);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) return null;
  if (cases.length === 0) return null;

  return (
    <>
      {/* Active Cases Panel - Blue background, white text */}
      <div 
        className="rounded-xl shadow-sm p-4 mb-6" 
        style={{ backgroundColor: '#2563eb' }}
        data-testid="active-cases-panel"
      >
        <h3 className="font-semibold text-lg mb-3 flex items-center gap-2 text-white">
          <MessageSquare className="w-5 h-5" />
          ACTIVE CASES
        </h3>
        <div className="space-y-2">
          {cases.map(caseItem => (
            <div
              key={caseItem.id}
              onClick={() => openCaseChat(caseItem)}
              className="p-3 rounded-lg cursor-pointer hover:bg-blue-500 transition-colors bg-blue-600/50"
              data-testid={`active-case-${caseItem.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {caseItem.status === 'open' ? (
                    <Clock className="w-4 h-4 text-yellow-300" />
                  ) : (
                    <MessageSquare className="w-4 h-4 text-green-300" />
                  )}
                  <span className="text-white text-sm font-medium">
                    {caseItem.status === 'open' ? 'Waiting for agronomist' : `With ${caseItem.assigned_agronomist_name || 'Agronomist'}`}
                  </span>
                </div>
                <span className={`px-2 py-0.5 text-xs rounded-full ${
                  caseItem.urgency === 'immediate' ? 'bg-red-400 text-red-900' :
                  caseItem.urgency === 'day' ? 'bg-yellow-400 text-yellow-900' : 'bg-blue-200 text-blue-900'
                }`}>
                  {caseItem.urgency}
                </span>
              </div>
              <p className="text-blue-100 text-sm mt-1 line-clamp-1">
                {caseItem.description}
              </p>
              <p className="text-blue-200 text-xs mt-1">
                {new Date(caseItem.created_at_utc).toLocaleDateString()}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Chat Modal */}
      {selectedCase && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-blue-600 text-white rounded-t-xl">
              <div>
                <h3 className="font-semibold">Case Chat</h3>
                <p className="text-sm text-blue-100">
                  {selectedCase.status === 'assigned' 
                    ? `Agronomist: ${selectedCase.assigned_agronomist_name}`
                    : 'Waiting for agronomist...'}
                </p>
              </div>
              <button onClick={closeCaseChat} className="p-2 hover:bg-blue-500 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Case Info */}
            <div className="p-3 bg-gray-50 border-b">
              <p className="text-sm text-gray-700">{selectedCase.description}</p>
              {selectedCase.photo_urls?.length > 0 && (
                <div className="flex gap-2 mt-2">
                  {selectedCase.photo_urls.map((url, i) => (
                    <img 
                      key={i} 
                      src={`${process.env.REACT_APP_BACKEND_URL}${url}`} 
                      alt="" 
                      className="w-12 h-12 object-cover rounded"
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px] max-h-[400px]">
              {messages.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  {selectedCase.status === 'assigned' 
                    ? 'No messages yet. Start the conversation!'
                    : 'Waiting for an agronomist to accept this case...'}
                </p>
              ) : (
                messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`p-3 rounded-lg max-w-[85%] ${
                      msg.sender_role === 'farmer'
                        ? 'bg-blue-100 ml-auto'
                        : 'bg-green-100'
                    }`}
                  >
                    <p className="text-xs font-medium text-gray-500 mb-1">{msg.sender_name}</p>
                    <p className="text-sm">{msg.text}</p>
                    {msg.image_urls?.length > 0 && (
                      <div className="flex gap-1 mt-2">
                        {msg.image_urls.map((url, i) => (
                          <img key={i} src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-16 h-16 object-cover rounded" />
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(msg.created_at_utc).toLocaleTimeString()}
                    </p>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input - only show if case is assigned */}
            {selectedCase.status === 'assigned' && (
              <div className="p-3 border-t">
                {/* Pending images preview */}
                {pendingImages.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {pendingImages.map((url, i) => (
                      <div key={i} className="relative">
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                          alt=""
                          className="w-16 h-16 object-cover rounded-lg border"
                        />
                        <button
                          onClick={() => removePendingImage(i)}
                          className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex gap-2">
                  {/* Hidden file input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handlePhotoUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  
                  {/* Photo button */}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="p-2 border rounded-lg hover:bg-gray-100 disabled:opacity-50 text-gray-600"
                    title="Add photo"
                    data-testid="chat-photo-btn"
                  >
                    {uploadingImage ? (
                      <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Camera className="w-5 h-5" />
                    )}
                  </button>
                  
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 px-4 py-2 border rounded-lg"
                    onKeyPress={(e) => e.key === 'Enter' && !sendingMessage && sendMessage()}
                    disabled={sendingMessage}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={sendingMessage || (!newMessage.trim() && pendingImages.length === 0)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// Component to display geo alerts from agronomists
export const GeoAlertsPanel = ({ farmId }) => {
  const { getAuthHeaders } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedAlert, setSelectedAlert] = useState(null);

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/farms/${farmId}/geo-alerts`, {
        headers: getAuthHeaders()
      });
      setAlerts(response.data);
    } catch (error) {
      console.error('Error fetching geo alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (farmId) {
      fetchAlerts();
    }
  }, [farmId]);

  const handleDismissAlert = async (alert) => {
    setDeletingId(alert.id);
    try {
      // Use PATCH to update status to dismissed
      await axios.patch(`${API}/farms/${farmId}/geo-alerts/${alert.id}`, 
        { status: 'dismissed' },
        { headers: getAuthHeaders() }
      );
      // Remove from local state
      setAlerts(prev => prev.filter(a => a.id !== alert.id));
      setSelectedAlert(null);
      toast.success('Alert dismissed');
    } catch (error) {
      // Fallback to delete if PATCH doesn't exist
      try {
        await axios.delete(`${API}/farms/${farmId}/geo-alerts/${alert.id}`, {
          headers: getAuthHeaders()
        });
        setAlerts(prev => prev.filter(a => a.id !== alert.id));
        setSelectedAlert(null);
        toast.success('Alert dismissed');
      } catch (err) {
        toast.error('Failed to dismiss alert');
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handleAlertClick = (alert, e) => {
    // Don't open modal if clicking delete button
    if (e.target.closest('button')) return;
    setSelectedAlert(alert);
  };

  if (loading || alerts.length === 0) return null;

  // Import AlertDetailModal dynamically to avoid circular deps
  const AlertDetailModal = require('./AlertDetailModal').default;

  return (
    <>
      <ImageModal imageUrl={enlargedImage} onClose={() => setEnlargedImage(null)} />
      {selectedAlert && (
        <AlertDetailModal 
          alert={selectedAlert}
          onClose={() => setSelectedAlert(null)}
          onDismiss={handleDismissAlert}
          dismissing={deletingId === selectedAlert?.id}
        />
      )}
      <div className="card-floating p-4 mb-6" data-testid="geo-alerts-panel">
        <h3 className="font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--text)' }}>
          <Users className="w-5 h-5" style={{ color: 'var(--success)' }} />
          Agronomist Alerts Nearby
          <span style={{ fontSize: 11, background: 'rgba(34,197,94,0.15)', color: 'var(--success)', padding: '2px 8px', borderRadius: 20 }}>
            {alerts.length}
          </span>
        </h3>
        <div className="space-y-3">
          {alerts.map(alert => (
            <div
              key={alert.id}
              onClick={(e) => handleAlertClick(alert, e)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleAlertClick(alert, e)}
              style={{
                padding: 16,
                borderRadius: 12,
                borderLeft: `4px solid ${alert.level === 'high' ? 'var(--danger)' : alert.level === 'regional' ? 'var(--primary)' : 'var(--warning)'}`,
                background: alert.level === 'high' ? 'rgba(239,68,68,0.1)' : alert.level === 'regional' ? 'rgba(59,130,246,0.1)' : 'rgba(245,158,11,0.1)',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
              data-testid={`geo-alert-${alert.id}`}
            >
              <div className="flex items-start gap-4">
                {/* Photo */}
                {alert.photo_url && (
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}${alert.photo_url}`} 
                    alt="Alert" 
                    className="w-24 h-24 object-cover rounded-lg flex-shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEnlargedImage(`${process.env.REACT_APP_BACKEND_URL}${alert.photo_url}`);
                    }}
                  />
                )}
                
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <p style={{ fontWeight: 600, color: alert.level === 'high' ? 'var(--danger)' : alert.level === 'regional' ? 'var(--primary)' : 'var(--warning)' }}>
                        {alert.subtype || alert.alert_type.replace('_', ' ')}
                      </p>
                      <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                        {alert.alert_type.replace('_', ' ')} • {alert.severity.replace('_', ' ')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span style={{
                        padding: '4px 10px',
                        fontSize: 11,
                        borderRadius: 20,
                        background: alert.level === 'high' ? 'rgba(239,68,68,0.2)' : alert.level === 'regional' ? 'rgba(59,130,246,0.2)' : 'rgba(245,158,11,0.2)',
                        color: alert.level === 'high' ? 'var(--danger)' : alert.level === 'regional' ? 'var(--primary)' : 'var(--warning)'
                      }}>
                        {alert.level === 'high' ? 'HIGH' : alert.level === 'regional' ? 'REGIONAL' : 'MEDIUM'}
                      </span>
                      {alert.level === 'regional' && (
                        <span style={{
                          padding: '4px 8px',
                          fontSize: 10,
                          borderRadius: 20,
                          background: 'rgba(100,116,139,0.2)',
                          color: 'var(--text-muted)'
                        }}>
                          Beyond 20km
                        </span>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDismissAlert(alert);
                        }}
                        disabled={deletingId === alert.id}
                        style={{
                          padding: 4,
                          borderRadius: 6,
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--text-muted)',
                          cursor: 'pointer'
                        }}
                        title="Dismiss alert"
                        data-testid={`dismiss-geo-alert-${alert.id}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 8 }}>{alert.message}</p>
                  
                  {/* Recommendations */}
                  {alert.recommendations && (
                    <div style={{ marginTop: 12, padding: 10, background: 'rgba(255,255,255,0.05)', borderRadius: 8, border: '1px solid var(--border-soft)' }}>
                      <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Recommendations:</p>
                      <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{alert.recommendations}</p>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-4 mt-2" style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {alert.distance_km} km away
                    </span>
                    <span>By: {alert.agronomist_name}</span>
                    <span>{new Date(alert.created_at_utc).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};


// Component for requesting agronomist visit
export const VisitRequestButton = () => {
  const { getAuthHeaders } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [showRequests, setShowRequests] = useState(false);
  const [requests, setRequests] = useState([]);
  const [requestWindow, setRequestWindow] = useState('3d');
  const [loading, setLoading] = useState(false);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/visit-requests`, {
        headers: getAuthHeaders()
      });
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching visit requests:', error);
    }
  };

  const handleCreateRequest = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/visit-requests`, {
        requested_window: requestWindow
      }, {
        headers: getAuthHeaders()
      });
      toast.success('Visit request submitted!');
      setShowModal(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create request');
    } finally {
      setLoading(false);
    }
  };

  const activeRequests = requests.filter(r => r.status !== 'visited');
  const hasScheduled = requests.some(r => r.status === 'accepted');

  return (
    <>
      <div className="flex gap-2">
        <button
          onClick={() => setShowModal(true)}
          className="btn-depth-primary flex items-center gap-2"
          data-testid="request-visit-btn"
        >
          <Calendar className="w-4 h-4" />
          Request Agronomist Visit
        </button>
        {activeRequests.length > 0 && (
          <button
            onClick={() => { setShowRequests(true); fetchRequests(); }}
            className="btn-depth-secondary flex items-center gap-2"
          >
            <Clock className="w-4 h-4" />
            {activeRequests.length}
          </button>
        )}
      </div>

      {/* Scheduled Visit Banner */}
      {hasScheduled && (
        <div className="mt-3 p-3 rounded-lg flex items-center gap-3" style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)' }}>
          <CheckCircle className="w-5 h-5" style={{ color: 'var(--success)' }} />
          <div>
            <p className="text-sm font-medium" style={{ color: 'var(--success)' }}>Agronomist visit scheduled!</p>
            {requests.filter(r => r.status === 'accepted').map(r => (
              <p key={r.id} className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                {r.assigned_agronomist_name} - {new Date(r.scheduled_datetime_utc).toLocaleString()}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Create Request Modal */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }}>
          <div className="card-floating max-w-md w-full p-6 anim-scale">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold" style={{ color: 'var(--text)' }}>Request Agronomist Visit</h3>
              <button onClick={() => setShowModal(false)} className="p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="label-depth">When do you need the visit?</label>
                <select
                  value={requestWindow}
                  onChange={(e) => setRequestWindow(e.target.value)}
                  className="input-depth"
                >
                  <option value="1d">Within 1 day (urgent)</option>
                  <option value="3d">Within 3 days</option>
                  <option value="7d">Within 7 days</option>
                </select>
              </div>

              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                An agronomist in your area will accept your request and schedule a visit.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={handleCreateRequest}
                  disabled={loading}
                  className="btn-depth-primary flex-1"
                >
                  {loading ? 'Submitting...' : 'Submit Request'}
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="btn-depth-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Requests Modal */}
      {showRequests && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }}>
          <div className="card-floating max-w-md w-full p-6 max-h-[80vh] overflow-y-auto anim-scale">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold" style={{ color: 'var(--text)' }}>Visit Requests</h3>
              <button onClick={() => setShowRequests(false)} className="p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>

            <div className="space-y-3">
              {requests.map(r => (
                <div
                  key={r.id}
                  className="p-3 rounded-lg"
                  style={{
                    background: r.status === 'accepted' ? 'rgba(34,197,94,0.1)' : r.status === 'visited' ? 'rgba(255,255,255,0.04)' : 'rgba(245,158,11,0.1)',
                    border: `1px solid ${r.status === 'accepted' ? 'rgba(34,197,94,0.3)' : r.status === 'visited' ? 'var(--border-soft)' : 'rgba(245,158,11,0.3)'}`
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {r.status === 'open' && <Clock className="w-4 h-4" style={{ color: 'var(--warning)' }} />}
                      {r.status === 'accepted' && <CheckCircle className="w-4 h-4" style={{ color: 'var(--success)' }} />}
                      {r.status === 'visited' && <CheckCircle className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />}
                      <span className="text-sm font-medium" style={{ color: r.status === 'visited' ? 'var(--text-muted)' : 'var(--text)' }}>
                        {r.status === 'open' ? 'Waiting for agronomist' :
                         r.status === 'accepted' ? 'Visit scheduled' : 'Completed'}
                      </span>
                    </div>
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {r.requested_window === '1d' ? 'Urgent' :
                       r.requested_window === '3d' ? 'Within 3 days' : 'Within 7 days'}
                    </span>
                  </div>
                  {r.status === 'accepted' && (
                    <div className="mt-2 text-sm" style={{ color: 'var(--success)' }}>
                      <p>{r.assigned_agronomist_name}</p>
                      <p className="font-medium">{new Date(r.scheduled_datetime_utc).toLocaleString()}</p>
                    </div>
                  )}
                  <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                    Requested: {new Date(r.created_at_utc).toLocaleDateString()}
                  </p>
                </div>
              ))}
              {requests.length === 0 && (
                <p className="text-center py-4" style={{ color: 'var(--text-muted)' }}>No visit requests yet</p>
              )}
            </div>
            
            <button
              onClick={() => setShowRequests(false)}
              className="btn-depth-secondary w-full mt-4"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default { GeoAlertsPanel, VisitRequestButton, ActiveCasesPanel };
